// Where a tile SITS — the grid geometry shared by every surface that renders a
// saved dashboard. Extracted from EditableGrid so the public share page
// (/r/:token) lays a shared report out exactly like the dashboard it came from:
// same 24-column grid on desktop, same read-order stack on a phone.
//
// This file owns the RULES (order, widths, mobile heights); each surface keeps
// its own renderer, because the app's grid draws the full authenticated tile
// chrome and the share page deliberately draws far less.

export const COLS = 24;        // mirrors Looker's grid units
export const ROW_HEIGHT = 30;
export const GRID_MARGIN = [8, 8];

// Single-number KPI tiles go 2-up on mobile. Bar gauges need the full width
// for their axis labels, so they're not included here.
export function isMetricTile(tile) {
  const vt = tile.vis?.type || '';
  return vt === 'single_value' || vt === 'single_value_period_over_period';
}

// "KPI-shaped" carousel card (number/gauge/text) — happy in a slim band.
// Mirrors Carousel.jsx's isKpiTile; anything else gets a full-width card there.
export function isKpiCard(tile) {
  const vt = tile.vis?.type || '';
  return tile.type === 'text' || isMetricTile(tile) || vt.includes('bar_gauge');
}

// Full-width tiles in the mobile stack don't need their tall desktop heights.
// Pick a compact height per tile type so metrics stay slim and charts/tables
// keep just enough room to read.
export function mobileTileHeight(tile) {
  const vt = tile.vis?.type || '';
  if (tile.type === 'text') return undefined; // size to content
  if (isMetricTile(tile)) return 120;
  if (vt.includes('bar_gauge')) return 104; // full-width, slim
  if (/looker_(column|bar|line|area|scatter|pie|donut)/.test(vt)) return 200;
  // Tables (and anything else): scale loosely with the configured rows, capped.
  // Give tables more height on mobile so there's room to read + a bigger target
  // to scroll within (less fighting with the page scroll).
  const isTable = tile.type === 'table' || vt === 'looker_grid' || vt.includes('table');
  return Math.min(isTable ? 440 : 320, Math.max(isTable ? 220 : 150, (tile.layout?.h ?? 6) * 20));
}

// Top-level items (tiles + full-width carousels) in READ ORDER: down the rows,
// then across. A carousel with no saved position sorts to the end, in the order
// it was added. This is the order the dashboard reads in, so it's the order a
// phone stack — and a shared report — must follow.
export function orderedItems(tiles = [], carousels = []) {
  return [
    ...tiles.map((t) => ({ kind: 'tile', el: t, y: t.layout?.y ?? 0, x: t.layout?.x ?? 0, w: t.layout?.w ?? 8, h: t.layout?.h ?? 6 })),
    ...carousels.map((c, idx) => ({ kind: 'carousel', el: c, y: c.layout?.y ?? (1000 + idx), x: 0, w: COLS, h: c.layout?.h ?? 7 })),
  ].sort((a, b) => a.y - b.y || a.x - b.x);
}

// Group consecutive tiles that share a desktop row (same y) so a strip of small
// KPI/header tiles stays on ONE row on mobile (shrinking to fit) instead of each
// dropping onto its own line. Wide tiles and carousels stand alone.
export function stackGroups(items) {
  const groups = [];
  let cur = null;
  for (const it of items) {
    if (it.kind === 'carousel') { groups.push({ type: 'carousel', it }); cur = null; continue; }
    if (cur && cur.y === it.y) cur.items.push(it);
    else { cur = { type: 'row', y: it.y, items: [it] }; groups.push(cur); }
  }
  return groups;
}

// A row of small tiles stays side by side on a phone only if there are at least
// two of them and each is no wider than half the grid.
export const keepsRow = (group) => group.items.length >= 2 && group.items.every((it) => it.w <= 12);

// react-grid-layout's layout array for a dashboard's tiles + carousels.
// Carousels are always full-width bands.
export function gridLayout(tiles = [], carousels = []) {
  return [
    ...tiles.map((t) => ({
      i: t.id,
      x: t.layout?.x ?? 0, y: t.layout?.y ?? 0, w: t.layout?.w ?? 8, h: t.layout?.h ?? 6,
      minW: 2, minH: 2,
    })),
    ...carousels.map((c, idx) => ({
      i: c.id,
      x: 0, y: c.layout?.y ?? (1000 + idx), w: COLS, h: c.layout?.h ?? 7,
      minW: COLS, maxW: COLS, minH: 3,
    })),
  ];
}

// ─── Reorder from a phone ────────────────────────────────────────────────────
// A phone has no grid to drag on (the editor's stack used to be read-only, so an
// imported dashboard in the wrong order simply could not be fixed from a phone).
// The editor offers ↑/↓ instead: move an item one step in READ ORDER, written
// back as desktop grid coordinates:
//   • a neighbour on the SAME desktop row → the two swap places within the span
//     they already occupy (no y changes, nothing else moves);
//   • first in its row going up (last going down) → it leaves that row and
//     becomes its own row just above the previous row (just below the next);
//     rows from there down are re-stacked so nothing overlaps. Rows above the
//     move keep their exact positions.
// Returns { [id]: {x,y,w,h} } for every item whose box changed (the shape the
// editor's applyLayout takes), or null when the item can't move that way.
export function moveInReadOrder(tiles = [], carousels = [], id, dir) {
  if (dir !== -1 && dir !== 1) return null;
  const rows = [];
  for (const it of orderedItems(tiles, carousels)) {
    const last = rows[rows.length - 1];
    if (last && last.y === it.y && it.kind === 'tile' && last.items[0].kind === 'tile') last.items.push(it);
    else rows.push({ y: it.y, items: [it] });
  }
  const r = rows.findIndex((row) => row.items.some((it) => it.el.id === id));
  if (r < 0) return null;
  const row = rows[r];
  const i = row.items.findIndex((it) => it.el.id === id);
  const a = row.items[i];
  const box = (it, patch) => ({ x: it.x, y: it.y, w: it.w, h: it.h, ...patch });

  // Same-row neighbour: the right one takes the left slot, the left one keeps
  // the pair's right edge (never closer than the new left tile's width).
  const j = i + dir;
  if (j >= 0 && j < row.items.length) {
    const [left, right] = dir < 0 ? [row.items[j], a] : [a, row.items[j]];
    return {
      [right.el.id]: box(right, { x: left.x }),
      [left.el.id]: box(left, { x: Math.max(left.x + right.w, right.x + right.w - left.w) }),
    };
  }

  // Across rows.
  const target = r + dir;
  if (target < 0 || target >= rows.length) return null;
  const from = Math.min(r, target);
  const startY = rows[from].y;
  const rest = row.items.filter((it) => it !== a);
  const next = rows.slice();
  next.splice(r, 1, ...(rest.length ? [{ y: row.y, items: rest }] : []));
  const at = dir < 0 ? target : (rest.length ? r + 2 : r + 1);
  next.splice(at, 0, { y: a.y, items: [a] });

  const out = {};
  let cursor = startY;
  for (let k = from; k < next.length; k++) {
    for (const it of next[k].items) if (it.y !== cursor) out[it.el.id] = box(it, { y: cursor });
    cursor += Math.max(...next[k].items.map((it) => it.h));
  }
  return Object.keys(out).length ? out : null;
}
